// The army and the deployment presets come from the engine, not from a copy of
// them here. `ARMY` is what a legal layout must contain and `RANKS` is the ladder
// in display order; restating either in the client is how a tray ends up
// offering a twenty-second standee the server will refuse. These subpaths are
// pure modules — no state, no RNG, no node builtins — so they bundle unchanged.
import { ARMY, RANKS, type Rank } from '@tira/engines/gera/pieces';
import { Insignia } from './geraArt.js';
import { FORMATION_NAMES } from '@tira/engines/gera/rules';
import { useEffect, useMemo, useState, type CSSProperties, type JSX } from 'react';
import { useT, type CopyKey, type T } from '../../i18n/index.js';
import { socket } from '../../net/socket.js';
import { useApp } from '../../state/store.js';
import { GameRoom } from '../parts/GameRoom.js';
import { useSteadyKeys } from '../lib/steady.js';
import './GeraBoard.css';

/**
 * S11 — Gera at the barangay outpost (design 1f, 2e, 4d). Presentation only, and
 * in this venue that is the entire product promise.
 *
 * THE SERVER IS THE ARBITER. The physical game needs a neutral human to hold
 * both sets of ranks, and a human cannot avoid learning them; here the engine
 * arbitrates and tells the table an OUTCOME. So this screen renders a clash as
 * pieces meeting, a burst, and the loser sliding off — and it prints a rank on a
 * fallen standee only when the payload actually carries one, which under the
 * shipped ruleset is only ever your own. There is no branch anywhere below that
 * guesses at a rank it was not sent; an enemy square is `{ seat }` and renders
 * as a velvet back, full stop.
 *
 * DEPLOYMENT IS SIMULTANEOUS-FEELING AND SEQUENCED UNDERNEATH — the engine asks
 * one seat at a time so that every wait has a real deadline (see the note on
 * turn() in the engine's index.ts). Neither layout is ever shown to the other,
 * and while a seat is still arranging the enemy's squares are not even
 * silhouettes, so the second seat cannot read the first layout's six gaps.
 *
 * THE PIECES ARE OURS. Brass-and-velvet standees with a two-character grade,
 * drawn in CSS (§G6 IP guardrails). Nothing here references anybody else's box
 * art, piece shapes or names, and the rank ids are the engine's own.
 *
 * STAGING. The desk is a nine-wide board of 32px squares lying flat in the felt,
 * and the pieces STAND ON IT: every standee carries `up`, which the room's
 * camera turns into the identity in top view and into an upright piece in the
 * 2.5D one. That is the venue's whole visual identity and the reason nothing in
 * this file mentions a camera — the board is described once.
 */

/* ── The view, as the engine publishes it ─────────────────────────────────── */

export type GeraPhase = 'deploy' | 'play';
export type ClashOutcome = 'mover' | 'holder' | 'both';

/**
 * A square as this audience sees it. `rank: null` on an occupied square is the
 * whole game: somebody is standing there and that is all you are entitled to.
 */
export type ViewCell = { seat: number; rank: Rank | null } | null;

export interface ClashView {
  ply: number;
  from: number;
  at: number;
  by: number;
  outcome: ClashOutcome;
  /** `rank` is present only for a standee this viewer was entitled to see. */
  fallen: { seat: number; rank: Rank | null }[];
}

export interface GeraOverView {
  reason: string;
  winner: number | null;
  drawBy: string | null;
  quitter: number | null;
  plies: number;
  counts: number[];
  clashes: number;
}

export type GeraAction =
  | { kind: 'deploy'; placements: { at: number; rank: Rank }[] }
  | { kind: 'move'; from: number; to: number }
  | { kind: 'resign' }
  | { kind: 'revealConsent' };

export interface GeraView {
  rows: number;
  cols: number;
  /** Seat index, or null on the rail. */
  you: number | null;
  board: ViewCell[];
  counts: number[];
  deployed: boolean[];
  /** Your zone and the row your watawat is walking to. Null for a miron. */
  home: { from: number; to: number; flagRow: number } | null;
  turn: number | null;
  phase: GeraPhase;
  clashes: ClashView[];
  /** An announced flag arrival waiting out its grace turn. Public — see FlagRun. */
  flagRun: { seat: number; at: number } | null;
  sinceClash: number;
  repeats: number;
  /** Tap targets, for the seat on the clock alone. */
  legal: GeraAction[];
  reveal: { consent: boolean[]; board: ({ seat: number; rank: Rank } | null)[] | null };
  over: GeraOverView | null;
}

/** A miron arrives with this seat index — no seat, no taps, no "mine". */
const SPECTATOR_SEAT = -1;

/** Exactly what a legal layout must place. Summed from the engine's own army. */
const ARMY_SIZE = Object.values(ARMY).reduce((n, k) => n + k, 0);

/** What the player is holding: a standee off the tray, or one off the desk. */
type Grab = { kind: 'rank'; rank: Rank } | { kind: 'cell'; at: number } | null;

/** One square, in table pixels. The mock's board is 9 × 32 wide (design 2e/4d). */
const CELL = 32;

export function GeraBoard(): JSX.Element {
  const match = useApp((s) => s.match);
  const t = useT();
  /** The layout being built, before it is laid out. Cell → rank. */
  const [layout, setLayout] = useState<Map<number, Rank>>(() => new Map());
  const [grab, setGrab] = useState<Grab>(null);
  const [from, setFrom] = useState<number | null>(null);
  /** Conceding is one tap to arm and one to commit — it gives away the pot. */
  const [armed, setArmed] = useState(false);

  const view = useMemo(() => readGeraView(match?.view), [match?.view]);

  // A new match, or the desk moving under a half-built move, invalidates both.
  useEffect(() => {
    setFrom(null);
    setGrab(null);
    setArmed(false);
  }, [match?.seq, match?.matchId]);

  useEffect(() => {
    setLayout(new Map());
  }, [match?.matchId]);

  if (!match || !view) {
    return <GameRoom felt={<p className="room__wait">{t('pusoy.gera.wait')}</p>} />;
  }

  const { rows, cols, board, counts, deployed, home, turn, phase, clashes, flagRun, legal, over } =
    view;
  const cells = rows * cols;
  const mySeat = match.seat > SPECTATOR_SEAT ? match.seat : null;
  const watching = mySeat === null;
  const myTurn = mySeat !== null && turn === mySeat && over === null;
  /** Seat 0 deploys along the top rows, so it plays the desk the other way up. */
  const flip = mySeat === 0;

  const deploying = phase === 'deploy';
  const iAmDeployed = mySeat !== null && deployed[mySeat] === true;
  /**
   * Setting up. The layout is local state until the whole 21 goes over in one
   * action, so a seat can arrange while the other one is still on the clock —
   * only the "Ilatag na" button waits for its turn. The three presets do not:
   * `legal` belongs to the seat being asked, which is the engine being right
   * about whose deadline is running.
   */
  const setup = deploying && !watching && !iAmDeployed;

  const presets = legal.filter(
    (a): a is Extract<GeraAction, { kind: 'deploy' }> => a.kind === 'deploy',
  );
  const moves = legal.filter((a): a is Extract<GeraAction, { kind: 'move' }> => a.kind === 'move');
  const sources = new Set(moves.map((m) => m.from));
  const targets = new Set(from === null ? [] : moves.filter((m) => m.from === from).map((m) => m.to));

  const placedCount = (rank: Rank): number => {
    let n = 0;
    for (const r of layout.values()) if (r === rank) n += 1;
    return n;
  };
  const laid = layout.size;

  const send = (action: GeraAction): void => {
    socket.send({ t: 'match.action', matchId: match.matchId, seq: match.seq, action });
    setFrom(null);
    setGrab(null);
  };

  const inHome = (cell: number): boolean =>
    home !== null && cell >= home.from && cell <= home.to;

  /** Setup taps: place, pick up, swap. Every result is inside the home zone. */
  const tapSetup = (cell: number): void => {
    if (!inHome(cell)) return;
    const here = layout.get(cell) ?? null;

    if (grab === null) {
      if (here !== null) setGrab({ kind: 'cell', at: cell });
      return;
    }

    const next = new Map(layout);
    if (grab.kind === 'rank') {
      // Dropping onto an occupied square trades: the standee already there goes
      // back to the tray, which is the only place a rank can wait.
      next.set(cell, grab.rank);
      setLayout(next);
      // Six tanod and two tiktik: keep holding the grade while copies remain, so
      // laying a wall is six taps rather than twelve.
      let placed = 0;
      for (const r of next.values()) if (r === grab.rank) placed += 1;
      setGrab(placed < ARMY[grab.rank] ? grab : null);
      return;
    }
    // Tapping the square you just lifted from puts that standee back in the
    // tray. Picking up and putting down are the same gesture twice, which is one
    // fewer control on a rail that has no room for another.
    if (grab.at === cell) {
      next.delete(cell);
      setLayout(next);
      setGrab(null);
      return;
    }
    const moving = layout.get(grab.at);
    if (moving === undefined) {
      setGrab(null);
      return;
    }
    next.delete(grab.at);
    if (here !== null) next.set(grab.at, here);
    next.set(cell, moving);
    setLayout(next);
    setGrab(null);
  };

  const tapPlay = (cell: number): void => {
    if (!myTurn) return;
    if (from === null) {
      if (sources.has(cell)) setFrom(cell);
      return;
    }
    if (cell === from) {
      setFrom(null);
      return;
    }
    if (targets.has(cell)) {
      send({ kind: 'move', from, to: cell });
      return;
    }
    // Tapping another of your own standees re-aims rather than doing nothing —
    // on a 32px square that is the difference between fluid and fiddly.
    if (sources.has(cell)) setFrom(cell);
  };

  const liftFromTray = (rank: Rank): void => {
    if (!setup) return;
    setGrab((cur) => (cur?.kind === 'rank' && cur.rank === rank ? null : { kind: 'rank', rank }));
  };

  /**
   * The clash to stage. Only the newest one, keyed on its ply so a remount
   * replays the burst exactly once — no timer, and nothing left smoking after
   * the desk has moved on.
   */
  const lastClash = clashes.length > 0 ? clashes[clashes.length - 1] ?? null : null;

  const revealed = view.reveal.board;
  const iConsented = mySeat !== null && view.reveal.consent[mySeat] === true;

  /**
   * Every square resolved once, in display order. Who is standing on a square
   * and what they are called is decided in exactly one place, so there is a
   * single line to audit for the thing this venue must never do.
   */
  const squares = Array.from({ length: cells }, (_, d) => {
    const idx = flip ? cells - 1 - d : d;
    const cell = board[idx] ?? null;
    const localRank = setup ? layout.get(idx) ?? null : null;
    const shown = revealed?.[idx] ?? null;
    return {
      d,
      idx,
      r: Math.floor(d / cols),
      c: d % cols,
      seat: shown ? shown.seat : localRank !== null ? mySeat : cell?.seat ?? null,
      rank: shown ? shown.rank : localRank ?? cell?.rank ?? null,
    };
  });

  /** Your own piece, so naming it in the toast discloses nothing. */
  const pickedRank = from === null ? null : board[from]?.rank ?? null;
  /** A miron has no army, so seat 1 is simply "the other one". */
  const mineSeat = mySeat ?? 0;
  const themSeat = mineSeat === 0 ? 1 : 0;

  const felt = (
    <div className="gera" style={{ '--cell': `${CELL}px` } as CSSProperties}>
      <div className="gera__desk">
        <div
          className="gera__grid"
          style={{ '--cols': cols, '--rows': rows } as CSSProperties}
          aria-label={t('pusoy.gera.desk')}
        >
          {squares.map((sq) => {
            const isHome = inHome(sq.idx);
            const canTap = setup
              ? isHome
              : myTurn &&
                (from === null ? sources.has(sq.idx) : targets.has(sq.idx) || sources.has(sq.idx));

            return (
              <button
                className="gera__sq"
                key={sq.d}
                data-home={setup && isHome}
                data-target={targets.has(sq.idx)}
                data-clash={targets.has(sq.idx) && sq.seat !== null}
                data-flagrun={flagRun?.at === sq.idx}
                disabled={!canTap}
                onClick={() => (setup ? tapSetup(sq.idx) : tapPlay(sq.idx))}
                aria-label={squareLabel(sq.idx, cols, sq.seat, sq.rank, mySeat, watching, t)}
              >
                {targets.has(sq.idx) && sq.seat === null ? (
                  <span className="gera__dot" aria-hidden="true" />
                ) : null}
              </button>
            );
          })}

          {/*
            The standees, one layer above the squares. They are siblings of the
            grid rather than children of the buttons because a standee stands UP
            — it leans off its own square towards the camera — while the square
            it belongs to is the flat thing you tap. Keeping the two apart is
            also what keeps a tap target from having to survive a 3D transform.
          */}
          <Men squares={squares} mineSeat={mineSeat} from={from} grab={grab} />

          {lastClash ? (
            <Clash
              key={`${lastClash.ply}:${lastClash.at}`}
              clash={lastClash}
              cols={cols}
              cells={cells}
              flip={flip}
            />
          ) : null}
        </div>
      </div>
    </div>
  );

  /**
   * The 21 standees waiting to be laid out, on the near rail — this is the one
   * moment in the venue when you are holding pieces rather than looking at a
   * desk, which is exactly what the rail is for.
   */
  const hand = setup ? (
    <div className="gera__tray">
      {RANKS.map((rank) => {
        const left = ARMY[rank] - placedCount(rank);
        return (
          <button
            className="gera__chip"
            key={rank}
            data-out={left === 0}
            data-held={grab?.kind === 'rank' && grab.rank === rank}
            disabled={left === 0}
            onClick={() => liftFromTray(rank)}
            aria-pressed={grab?.kind === 'rank' && grab.rank === rank}
            aria-label={t('pusoy.gera.tray', { rank: rankName(rank, t), n: left })}
          >
            {/*
              The standee as the spec draws it (1f): brass frame, velvet panel,
              the rank's own insignia, and the two-tier foot it stands on. The
              code stays under the mark because two pairs in this army differ by
              metal alone — silver leaf against gold, silver bar against gold —
              and colour is never allowed to be the only difference (§7.3).
            */}
            <span className="gera__standee">
              <span className="gera__chip-face">
                <Insignia rank={rank} />
                <b className="data">{rankCode(rank, t)}</b>
              </span>
              <span className="gera__riser" />
              <span className="gera__foot" />
            </span>
            <em className="data">{left}</em>
          </button>
        );
      })}
    </div>
  ) : undefined;

  return (
    <GameRoom
      felt={felt}
      hand={hand}
      toast={
        over ? (
          verdictLine(over, mySeat, watching, t)
        ) : flagRun ? (
          /*
           * The engine's one deliberate rank disclosure — the defender gets
           * exactly one turn to answer, and a grace turn nobody can see is not a
           * grace turn — so it takes the loudest line on the screen.
           */
          <>
            <b>{t('pusoy.gera.flag.title')}</b>
            {/*
              A miron has no seat, so `flagRun.seat === mySeat` was false for
              them and they were told "this is your only chance to take it" —
              addressed as a defender at a table they are not sitting at.
            */}
            {watching
              ? t('pusoy.gera.flag.watch', { n: flagRun.seat + 1 })
              : flagRun.seat === mySeat
                ? t('pusoy.gera.flag.yours')
                : t('pusoy.gera.flag.theirs')}
          </>
        ) : setup ? (
          setupLine(laid, myTurn, grab, t)
        ) : from !== null ? (
          <>
            <b>{pickedRank ? rankName(pickedRank, t) : t('pusoy.gera.piece')}</b>{' '}
            {t('pusoy.gera.picked')}
          </>
        ) : (
          turnLine(view, mySeat, watching, t)
        )
      }
      actions={
        setup ? (
          <>
            <button
              className="room-act"
              data-tone="primary"
              type="button"
              disabled={laid !== ARMY_SIZE || !myTurn}
              onClick={() =>
                send({
                  kind: 'deploy',
                  placements: [...layout.entries()]
                    .map(([at, rank]) => ({ at, rank }))
                    .sort((a, b) => a.at - b.at),
                })
              }
            >
              {t('pusoy.gera.act.deploy')}
            </button>
            <button
              className="room-act"
              type="button"
              disabled={laid === 0}
              onClick={() => setLayout(new Map())}
            >
              {t('pusoy.gera.act.clear')}
            </button>
          </>
        ) : over ? (
          /*
           * The endgame reveal, and it is consent rather than a stage: both seats
           * must ask for it, it is one-way, and it is offered only once the match
           * is finished — a reveal on a live desk would BE the ending. Only
           * offered to somebody holding a chair.
           */
          !watching && revealed === null && !iConsented ? (
            <button className="room-act" type="button" onClick={() => send({ kind: 'revealConsent' })}>
              {t('pusoy.gera.act.reveal')}
            </button>
          ) : null
        ) : from !== null ? (
          <button className="room-act" type="button" onClick={() => setFrom(null)}>
            {t('pusoy.gera.act.back')}
          </button>
        ) : !watching ? (
          /*
           * Conceding. The engine has had `resign` since it shipped — vectored,
           * with its own event and verdict copy — and no client ever sent it, so
           * the only way out of a losing position was to leave the table, which
           * forfeits identically but also drops you out of the rematch.
           *
           * Two taps, because it hands the whole escrowed pot to the opponent.
           */
          <button
            className="room-act"
            data-tone={armed ? 'danger' : undefined}
            type="button"
            onClick={() => (armed ? send({ kind: 'resign' }) : setArmed(true))}
            onBlur={() => setArmed(false)}
          >
            {t(armed ? 'pusoy.gera.act.resignSure' : 'pusoy.gera.act.resign')}
          </button>
        ) : null
      }
      aside={
        /*
         * The mock hangs this card off the right edge (design 2e, left:614). The
         * room camera swings the desk's far corner out to roughly x=740, so the
         * card moves to the matching band on the left, where both cameras leave
         * it clear of the wood. Materials are the mock's, unchanged.
         */
        <div className="gera__side">
          {setup ? (
            <div className="gera__card">
              <h3>{t('pusoy.gera.presets.title')}</h3>
              <div className="gera__presets">
                {presets.map((action, i) => {
                  const key = FORMATION_NAMES[i] ?? `preset-${i}`;
                  const meta = FORMATION_LABEL[key];
                  return (
                    <button
                      className="gera__preset"
                      key={key}
                      onClick={() => {
                        setLayout(new Map(action.placements.map((p) => [p.at, p.rank])));
                        setGrab(null);
                      }}
                      title={meta ? t(meta.note) : ''}
                    >
                      {meta ? t(meta.name) : t('pusoy.gera.preset.n', { n: i + 1 })}
                    </button>
                  );
                })}
              </div>
              <p className="gera__note">{t('pusoy.gera.presets.note')}</p>
            </div>
          ) : (
            <div className="gera__card">
              <h3>{t('pusoy.gera.army')}</h3>
              <div className="gera__tally">
                <span>
                  {watching ? t('pusoy.seat.n', { n: 1 }) : t('pusoy.gera.army.mine')}
                </span>
                <b className="data">{counts[mineSeat] ?? 0}</b>
              </div>
              <div className="gera__tally" data-them="true">
                <span>
                  {watching ? t('pusoy.seat.n', { n: 2 }) : t('pusoy.gera.army.foe')}
                </span>
                <b className="data">{counts[themSeat] ?? 0}</b>
              </div>

              {phase === 'play' ? (
                <p className="gera__note">
                  {t('pusoy.gera.tally', { n: clashes.length, m: view.sinceClash })}
                  {view.repeats > 1 ? t('pusoy.gera.tally.repeat', { n: view.repeats }) : ''}
                </p>
              ) : null}

              <details className="gera__ladder">
                <summary>{t('pusoy.gera.ladder')}</summary>
                <ol>
                  {RANKS.map((rank) => (
                    <li key={rank}>
                      <Insignia rank={rank} />
                      <span>{rankName(rank, t)}</span>
                    </li>
                  ))}
                </ol>
                <p>{t('pusoy.gera.ladder.note')}</p>
              </details>

              {over === null || watching ? null : revealed ? (
                <p className="gera__note">{t('pusoy.gera.reveal.done')}</p>
              ) : iConsented ? (
                <p className="gera__note">{t('pusoy.gera.reveal.waiting')}</p>
              ) : (
                <p className="gera__note">{t('pusoy.gera.reveal.offer')}</p>
              )}
            </div>
          )}
        </div>
      }
    />
  );
}

/* ── The standee ──────────────────────────────────────────────────────────── */

/**
 * A brass-and-velvet standee (design 1f / 4d). It carries `up`, so the room's
 * camera stands it on its square and the top camera lays it flat again — one
 * piece of markup, both views.
 *
 * `rank: null` is the velvet back everybody else's pieces wear. There is no
 * variant of this component that renders a rank it was not handed, which is what
 * keeps a leak from being one careless prop away.
 */
/**
 * The standee layer, with its identity. Keys come from useSteadyKeys rather
 * than the cell, so a standee that walks a square keeps its DOM node and the
 * CSS transition on .gera__pc carries it there — remounting was why every
 * move used to teleport. A component of its own because the hook must run
 * unconditionally and the board has an early return above this layer.
 */
function Men({
  squares,
  mineSeat,
  from,
  grab,
}: {
  squares: { d: number; idx: number; r: number; c: number; seat: number | null; rank: Rank | null }[];
  mineSeat: number;
  from: number | null;
  grab: { kind: string; at?: number } | null;
}): JSX.Element {
  const keys = useSteadyKeys(
    squares.map((sq) => (sq.seat === null ? null : sq)),
    (sq) => sq.seat ?? 0,
  );
  return (
    <div className="gera__men" aria-hidden="true">
      {squares.map((sq) =>
        sq.seat === null ? null : (
          <Standee
            key={keys[sq.d] === '' ? `d${sq.d}` : keys[sq.d]}
            rank={sq.rank}
            /*
             * `mineSeat`, not `mySeat`: a miron has no army, and comparing
             * against null painted both of them pula. Once the two sides
             * intermingle that board cannot be read at all — while the
             * card beside it names Upuan 1 and Upuan 2 in two colours.
             */
            mine={sq.seat === mineSeat}
            r={sq.r}
            c={sq.c}
            picked={from === sq.idx || (grab?.kind === 'cell' && grab.at === sq.idx)}
          />
        ),
      )}
    </div>
  );
}

function Standee({
  rank,
  mine,
  r,
  c,
  picked,
}: {
  rank: Rank | null;
  mine: boolean;
  r: number;
  c: number;
  picked: boolean;
}): JSX.Element {
  const t = useT();
  return (
    <span
      className="gera__pc up"
      style={{ '--r': r, '--c': c } as CSSProperties}
      data-mine={mine}
      data-back={rank === null}
      data-picked={picked}
    >
      {/*
        A rank here is the arbiter having decided this viewer is entitled to it
        — under the shipped ruleset, your own army and nothing else. So the
        insignia rides along with it: `rank === null` is an enemy standee and
        stays bare velvet, which is the whole game. The mark cannot leak what
        the payload never carried.
      */}
      <span className="gera__pc-face">
        {rank === null ? null : (
          <>
            <Insignia rank={rank} />
            <b className="data">{rankCode(rank, t)}</b>
          </>
        )}
      </span>
      <span className="gera__pc-foot" />
    </span>
  );
}

/* ── The clash ────────────────────────────────────────────────────────────── */

/**
 * Pieces meet, a burst, the loser slides off (§G6). The burst lies flat on the
 * square — it is a flash on the desk, and reads as one from either camera —
 * while a fallen standee is still a standee, so it keeps `up` and does its
 * falling inside that.
 *
 * What this deliberately does NOT do is turn anything over: a fallen standee
 * shows a rank only when the payload carries one, which under the shipped
 * ruleset means your own pieces and nothing else. `fallen[].rank` being null is
 * the arbiter declining to say, and this renders that silence rather than
 * filling it in.
 */
function Clash({
  clash,
  cols,
  cells,
  flip,
}: {
  clash: ClashView;
  cols: number;
  cells: number;
  flip: boolean;
}): JSX.Element {
  const t = useT();
  const disp = flip ? cells - 1 - clash.at : clash.at;
  const style = { '--r': Math.floor(disp / cols), '--c': disp % cols } as CSSProperties;
  return (
    <span className="gera__clash" style={style} data-outcome={clash.outcome} aria-hidden="true">
      <span className="gera__burst" />
      {clash.fallen.map((f, i) => (
        <span className="gera__fell up" key={i} data-side={i === 0 ? 'a' : 'b'}>
          <span className="gera__fell-in">
            {f.rank ? (
              <>
                <Insignia rank={f.rank} />
                <b className="data">{rankCode(f.rank, t)}</b>
              </>
            ) : null}
          </span>
        </span>
      ))}
    </span>
  );
}

/* ── Rank ids, read apart ─────────────────────────────────────────────────── */

/**
 * Ids are `"heneral-5"`, `"opisyal-3"`, `"tanod"`, `"tiktik"`, `"watawat"`
 * (packages/engines/src/gera/pieces.ts). Splitting one is parsing, not a rule —
 * nothing below decides who wins a clash, only what a standee is called and
 * which mark it wears. The arbiter is the server's.
 */
type Family = 'heneral' | 'opisyal' | 'tanod' | 'tiktik' | 'watawat';

function familyOf(rank: Rank): Family {
  const dash = rank.indexOf('-');
  return (dash < 0 ? rank : rank.slice(0, dash)) as Family;
}

function gradeOf(rank: Rank): number {
  const dash = rank.indexOf('-');
  return dash < 0 ? 0 : Number(rank.slice(dash + 1));
}

const FAMILY_CODE: Record<Family, CopyKey> = {
  heneral: 'pusoy.gera.code.heneral',
  opisyal: 'pusoy.gera.code.opisyal',
  tanod: 'pusoy.gera.code.tanod',
  tiktik: 'pusoy.gera.code.tiktik',
  watawat: 'pusoy.gera.code.watawat',
};

const FAMILY_NAME: Record<Family, CopyKey> = {
  heneral: 'pusoy.gera.rank.heneral',
  opisyal: 'pusoy.gera.rank.opisyal',
  tanod: 'pusoy.gera.rank.tanod',
  tiktik: 'pusoy.gera.rank.tiktik',
  watawat: 'pusoy.gera.rank.watawat',
};

function rankCode(rank: Rank, t: T): string {
  const code = t(FAMILY_CODE[familyOf(rank)]);
  const grade = gradeOf(rank);
  return grade > 0 ? t('pusoy.gera.code.graded', { code, grade }) : code;
}

function rankName(rank: Rank, t: T): string {
  const name = t(FAMILY_NAME[familyOf(rank)]);
  const grade = gradeOf(rank);
  return grade > 0 ? t('pusoy.gera.rank.graded', { name, grade }) : name;
}

/** The engine's three presets, named. The order is FORMATION_NAMES', not ours. */
const FORMATION_LABEL: Record<string, { name: CopyKey; note: CopyKey }> = {
  harang: { name: 'pusoy.gera.form.harang', note: 'pusoy.gera.form.harang.note' },
  kuta: { name: 'pusoy.gera.form.kuta', note: 'pusoy.gera.form.kuta.note' },
  sugod: { name: 'pusoy.gera.form.sugod', note: 'pusoy.gera.form.sugod.note' },
};

/* ── Reading the engine's view ────────────────────────────────────────────── */

/**
 * The view arrives as `unknown` (the wire type must not know the rules), so it
 * is narrowed rather than cast. A malformed payload renders the waiting state
 * instead of throwing inside a live match.
 *
 * `rank` is read as "present or absent", never defaulted: a missing rank on an
 * occupied square is the partition doing its job, and inventing one here would
 * be the leak the whole engine is built to prevent.
 */
export function readGeraView(raw: unknown): GeraView | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const v = raw as Record<string, unknown>;
  const rows = isNum(v['rows']) ? v['rows'] : 8;
  const cols = isNum(v['cols']) ? v['cols'] : 9;
  const rawBoard = v['board'];
  if (!Array.isArray(rawBoard) || rawBoard.length !== rows * cols) return null;

  const rawReveal =
    typeof v['reveal'] === 'object' && v['reveal'] !== null
      ? (v['reveal'] as Record<string, unknown>)
      : {};

  return {
    rows,
    cols,
    you: isNum(v['you']) ? v['you'] : null,
    board: rawBoard.map(readCell),
    counts: numList(v['counts']),
    deployed: boolList(v['deployed']),
    home: readHome(v['home']),
    turn: isNum(v['turn']) ? v['turn'] : null,
    phase: v['phase'] === 'play' ? 'play' : 'deploy',
    clashes: Array.isArray(v['clashes'])
      ? v['clashes'].map(readClash).filter((c): c is ClashView => c !== null)
      : [],
    flagRun: readFlagRun(v['flagRun']),
    sinceClash: isNum(v['sinceClash']) ? v['sinceClash'] : 0,
    repeats: isNum(v['repeats']) ? v['repeats'] : 0,
    legal: Array.isArray(v['legal'])
      ? v['legal'].map(readAction).filter((a): a is GeraAction => a !== null)
      : [],
    reveal: {
      consent: boolList(rawReveal['consent']),
      board: Array.isArray(rawReveal['board'])
        ? rawReveal['board'].map((c) => {
            const cell = readCell(c);
            return cell && cell.rank ? { seat: cell.seat, rank: cell.rank } : null;
          })
        : null,
    },
    over: readOver(v['over']),
  };
}

function readCell(raw: unknown): ViewCell {
  if (typeof raw !== 'object' || raw === null) return null;
  const c = raw as Record<string, unknown>;
  if (!isNum(c['seat'])) return null;
  return { seat: c['seat'], rank: readRank(c['rank']) };
}

function readRank(raw: unknown): Rank | null {
  return typeof raw === 'string' && (RANKS as readonly string[]).includes(raw) ? (raw as Rank) : null;
}

function readHome(raw: unknown): GeraView['home'] {
  if (typeof raw !== 'object' || raw === null) return null;
  const h = raw as Record<string, unknown>;
  if (!isNum(h['from']) || !isNum(h['to'])) return null;
  return { from: h['from'], to: h['to'], flagRow: isNum(h['flagRow']) ? h['flagRow'] : 0 };
}

function readClash(raw: unknown): ClashView | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const c = raw as Record<string, unknown>;
  if (!isNum(c['at']) || !isNum(c['by'])) return null;
  const outcome = c['outcome'];
  return {
    ply: isNum(c['ply']) ? c['ply'] : 0,
    from: isNum(c['from']) ? c['from'] : -1,
    at: c['at'],
    by: c['by'],
    outcome: outcome === 'mover' || outcome === 'holder' || outcome === 'both' ? outcome : 'both',
    fallen: Array.isArray(c['fallen'])
      ? c['fallen'].flatMap((f) => {
          const cell = readCell(f);
          return cell ? [{ seat: cell.seat, rank: cell.rank }] : [];
        })
      : [],
  };
}

function readFlagRun(raw: unknown): GeraView['flagRun'] {
  if (typeof raw !== 'object' || raw === null) return null;
  const f = raw as Record<string, unknown>;
  if (!isNum(f['seat']) || !isNum(f['at'])) return null;
  return { seat: f['seat'], at: f['at'] };
}

function readAction(raw: unknown): GeraAction | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const a = raw as Record<string, unknown>;
  if (a['kind'] === 'move' && isNum(a['from']) && isNum(a['to'])) {
    return { kind: 'move', from: a['from'], to: a['to'] };
  }
  if (a['kind'] === 'deploy' && Array.isArray(a['placements'])) {
    const placements = a['placements'].flatMap((p) => {
      if (typeof p !== 'object' || p === null) return [];
      const e = p as Record<string, unknown>;
      const rank = readRank(e['rank']);
      return isNum(e['at']) && rank ? [{ at: e['at'], rank }] : [];
    });
    return placements.length > 0 ? { kind: 'deploy', placements } : null;
  }
  return null;
}

function readOver(raw: unknown): GeraOverView | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const o = raw as Record<string, unknown>;
  return {
    reason: typeof o['reason'] === 'string' ? o['reason'] : 'draw',
    winner: isNum(o['winner']) ? o['winner'] : null,
    drawBy: typeof o['drawBy'] === 'string' ? o['drawBy'] : null,
    quitter: isNum(o['quitter']) ? o['quitter'] : null,
    plies: isNum(o['plies']) ? o['plies'] : 0,
    counts: numList(o['counts']),
    clashes: isNum(o['clashes']) ? o['clashes'] : 0,
  };
}

const isNum = (x: unknown): x is number => typeof x === 'number' && Number.isFinite(x);
const numList = (x: unknown): number[] => (Array.isArray(x) ? x.filter(isNum) : []);
const boolList = (x: unknown): boolean[] => (Array.isArray(x) ? x.map((b) => b === true) : []);

/* ── Copy ─────────────────────────────────────────────────────────────────── */

const FILES = 'ABCDEFGHI';

function squareLabel(
  cell: number,
  cols: number,
  seat: number | null,
  rank: Rank | null,
  mySeat: number | null,
  watching: boolean,
  t: T,
): string {
  const name = `${FILES[cell % cols] ?? '?'}${Math.floor(cell / cols) + 1}`;
  if (seat === null) return name;
  if (rank !== null) return t('pusoy.gera.sq', { sq: name, what: rankName(rank, t) });
  const who = watching
    ? t('pusoy.gera.piece.seat', { n: seat + 1 })
    : seat === mySeat
      ? t('pusoy.gera.piece.mine')
      : t('pusoy.gera.piece.foe');
  return t('pusoy.gera.sq', { sq: name, what: who });
}

function setupLine(laid: number, myTurn: boolean, grab: Grab, t: T): JSX.Element {
  if (laid === ARMY_SIZE && !myTurn) {
    return <>{t('pusoy.gera.setup.ready')}</>;
  }
  if (grab === null) {
    return (
      <>
        <b>
          {laid}/{ARMY_SIZE}
        </b>{' '}
        {t('pusoy.gera.setup.count')}
      </>
    );
  }
  return grab.kind === 'rank' ? (
    <>{t('pusoy.gera.setup.drop')}</>
  ) : (
    <>{t('pusoy.gera.setup.move')}</>
  );
}

function turnLine(v: GeraView, mySeat: number | null, watching: boolean, t: T): string {
  if (v.over !== null) return t('pusoy.gera.over');
  if (v.phase === 'deploy') {
    if (watching) return t('pusoy.gera.turn.deployAll');
    return t(v.turn === mySeat ? 'pusoy.gera.turn.deployYou' : 'pusoy.gera.turn.deployFoe');
  }
  if (v.turn === null) return t('pusoy.gera.turn.wait');
  if (watching) return t('pusoy.gera.turn.seat', { n: v.turn + 1 });
  return t(v.turn === mySeat ? 'pusoy.gera.turn.you' : 'pusoy.gera.turn.foe');
}

const OVER_REASON: Record<string, CopyKey> = {
  flagCaptured: 'pusoy.gera.why.flagCaptured',
  flagHome: 'pusoy.gera.why.flagHome',
  noMoves: 'pusoy.gera.why.noMoves',
  resign: 'pusoy.gera.why.resign',
  draw: 'pusoy.gera.why.draw',
  forfeit: 'pusoy.gera.why.forfeit',
};

function verdictLine(over: GeraOverView, mySeat: number | null, watching: boolean, t: T): string {
  const reason = OVER_REASON[over.reason];
  const why = reason ? t(reason) : t('pusoy.gera.over');
  if (over.winner === null) {
    const how = over.drawBy === 'repetition' ? t('pusoy.gera.draw.repetition') : over.drawBy === 'noProgress' ? t('pusoy.gera.draw.noProgress') : '';
    return t('pusoy.gera.over.draw', { why, how });
  }
  if (watching) return t('pusoy.gera.over.seat', { why, n: over.winner + 1 });
  return over.winner === mySeat
    ? t('pusoy.gera.over.you', { why })
    : t('pusoy.gera.over.foe', { why });
}
